import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguageStore } from "@/store/languageStore";
import logoWide from "@/assets/trata-health-logo-wide.png";

const LANGS = [
  { code: "en", flag: "🇬🇧", nameKey: "english", nativeKey: "englishNative" },
  { code: "hi", flag: "🇮🇳", nameKey: "hindi", nativeKey: "hindiNative" },
];

export default function LanguageSelect() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const language = useLanguageStore((s) => s.language);
  const setLanguage = useLanguageStore((s) => s.setLanguage);
  const markLanguageSelected = useLanguageStore((s) => s.markLanguageSelected);
  const [selected, setSelected] = useState(language || "en");

  const pick = (code) => {
    setSelected(code);
    setLanguage(code);
  };

  const handleContinue = () => {
    setLanguage(selected);
    markLanguageSelected();
    navigate("/auth", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-between bg-background px-6 py-10">
      <div className="w-full max-w-sm flex-1 flex flex-col justify-center">
        <div className="flex flex-col items-center">
          <div className="h-28 w-28 rounded-2xl bg-card border border-border flex items-center justify-center p-3 shadow-sm">
            <img src={logoWide} alt="Trata Health" className="h-full w-auto object-contain" />
          </div>
          <p className="mt-3 text-sm font-medium text-muted-foreground text-center">
            {t("languageSelect.tagline")}
          </p>
        </div>

        <div className="mt-10 text-center">
          <h1 className="text-2xl font-semibold text-foreground">{t("languageSelect.title")}</h1>
          <p className="text-base font-medium text-muted-foreground mt-1">{t("languageSelect.titleHindi")}</p>
        </div>

        <div className="mt-8 space-y-3">
          {LANGS.map((l) => {
            const active = selected === l.code;
            return (
              <button
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                onClick={() => pick(l.code)}
                className={`w-full flex items-center gap-4 rounded-2xl border-2 px-5 py-4 transition-colors ${
                  active ? "border-primary bg-secondary" : "border-border bg-card"
                }`}
              >
                <span className="text-3xl">{l.flag}</span>
                <div className="flex-1 text-left">
                  <p className={`text-lg font-semibold ${active ? "text-primary" : "text-foreground"}`}>
                    {t(`languageSelect.${l.nameKey}`)}
                  </p>
                  <p className="text-sm text-muted-foreground">{t(`languageSelect.${l.nativeKey}`)}</p>
                </div>
                {active && (
                  <span className="h-7 w-7 rounded-full bg-primary flex items-center justify-center">
                    <Check className="h-4 w-4 text-primary-foreground" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <p className="mt-5 text-center text-xs text-muted-foreground">{t("languageSelect.note")}</p>
      </div>

      <div className="w-full max-w-sm">
        <Button
          className="w-full h-12 rounded-2xl text-base gap-2"
          onClick={handleContinue}
          data-testid="lang-continue"
        >
          {t("languageSelect.continueBtn")}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
